"use client";

import { useState, type ReactNode } from "react";
import {
  BORDER_HI,
  TEXT,
  VIOLET_BRIGHT,
  VIOLET_GLOW,
} from "@/components/ui/organisms/chord-practice/palette";

interface TermProps {
  /** The technical word being defined. */
  children: ReactNode;
  /** Short, accessible explanation shown on hover/focus. */
  tip: ReactNode;
}

/**
 * Inline lexicon term : highlights a technical word with a dotted underline
 * and reveals a definition on hover/focus. Used to make harmony content
 * approachable for non-musicians without cluttering the page with prose.
 */
export function Term({ children, tip }: TermProps) {
  const [open, setOpen] = useState(false);

  return (
    <span
      style={{
        position: "relative",
        display: "inline-block",
        cursor: "help",
        textDecoration: "underline",
        textDecorationStyle: "dotted",
        textDecorationColor: "rgba(196,181,253,0.5)",
        textUnderlineOffset: 3,
        color: VIOLET_BRIGHT,
      }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      tabIndex={0}
    >
      {children}
      {open && (
        <span
          role="tooltip"
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 50,
            minWidth: 180,
            maxWidth: 280,
            padding: "10px 12px",
            borderRadius: 8,
            background: "rgba(15,15,22,0.98)",
            border: `1px solid ${BORDER_HI}`,
            boxShadow: `0 8px 24px rgba(0,0,0,0.5), 0 0 0 1px ${VIOLET_GLOW}`,
            fontFamily: "Inter, sans-serif",
            fontSize: 12,
            fontWeight: 400,
            color: TEXT,
            lineHeight: 1.5,
            letterSpacing: "0",
            textTransform: "none",
            textDecoration: "none",
            textAlign: "left",
            pointerEvents: "none",
          }}
        >
          {tip}
          <span
            aria-hidden
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "5px solid transparent",
              borderRight: "5px solid transparent",
              borderTop: `5px solid ${BORDER_HI}`,
            }}
          />
        </span>
      )}
    </span>
  );
}

/**
 * Centralized definitions so a term has one canonical explanation across
 * the app. Keep them short (1-2 sentences) and music-teacher friendly.
 */
export const LEXICON = {
  degree: (
    <>
      Une <strong>position</strong> dans la gamme. Le degré 1 est la tonique
      (la note centrale), le 5e est la dominante, le 7e la sensible…
    </>
  ),
  tonic: (
    <>
      La note de référence d&apos;une tonalité. C&apos;est elle qui donne son
      nom à la gamme (ex. <em>Do</em> dans Do majeur).
    </>
  ),
  sensible: (
    <>
      Le 7<sup>e</sup> degré quand il est à un demi-ton sous la tonique. Sa
      proximité avec la tonique crée une attraction forte qui «&nbsp;appelle&nbsp;»
      la résolution.
    </>
  ),
  third: (
    <>
      Intervalle de 3 notes (ex. Do→Mi). La <strong>tierce majeure</strong>{" "}
      vaut 2 tons, la <strong>tierce mineure</strong> 1 ton et demi, c&apos;est
      ce qui détermine la couleur d&apos;un accord.
    </>
  ),
  cadence: (
    <>
      Un enchaînement d&apos;accords qui crée un point de repos ou de tension.
      La cadence parfaite (V → I) est la plus emblématique.
    </>
  ),
  function: (
    <>
      Le <strong>rôle</strong> d&apos;un accord dans une tonalité : tonique
      (foyer), sous-dominante (départ) ou dominante (tension qui veut
      résoudre).
    </>
  ),
  triad: (
    <>
      Accord de 3 notes obtenu en empilant des tierces (1·3·5). C&apos;est la
      brique de base de l&apos;harmonie tonale.
    </>
  ),
  semitone: (
    <>
      La plus petite distance entre deux notes, deux touches voisines au
      piano (ex. Mi→Fa, Si→Do).
    </>
  ),
  tone: (
    <>
      Distance de 2 demi-tons (ex. Do→Ré). C&apos;est l&apos;intervalle le
      plus courant entre deux degrés consécutifs d&apos;une gamme.
    </>
  ),
  pattern: (
    <>
      La suite des intervalles (T et ½) qui construit la gamme. Toutes les
      gammes majeures partagent le même : <strong>T-T-½-T-T-T-½</strong>.
    </>
  ),
} as const;
